import { Eyebrow, MaskLine, Reveal } from "@/components/Reveal";

const LegalLayout = ({ eyebrow, title, children }) => (
  <>
    <section className="cta-glow pb-20 pt-44">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Eyebrow className="text-gold">{eyebrow}</Eyebrow>
        <h1 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          <MaskLine delay={0.15}>{title}</MaskLine>
        </h1>
      </div>
    </section>
    <section className="py-20 sm:py-24">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal>
          <div className="space-y-10 text-base leading-relaxed text-fog">{children}</div>
        </Reveal>
      </div>
    </section>
  </>
);

const H = ({ children }) => (
  <h2 className="font-heading text-2xl font-bold tracking-tight text-ink">{children}</h2>
);

export const PrivacyPolicy = () => (
  <LegalLayout eyebrow="Legal" title="Privacy Policy">
    <p>
      This Privacy Policy describes how Sunkev Enterprises Private Limited ("Sunkev
      Enterprises", "we", "us", "our") collects, uses and protects information when you use
      our website and our platform, Craftsi. By using our website or services, you agree to
      the practices described in this policy.
    </p>

    <div className="space-y-4">
      <H>1. Information We Collect</H>
      <p>
        We may collect information you provide directly to us, such as your name, email
        address, phone number, company name and the contents of messages you send through
        our contact forms. When you use the Craftsi platform, we may additionally collect
        information required to provide marketplace services, such as profile details,
        service listings, bookings and transaction-related information.
      </p>
    </div>

    <div className="space-y-4">
      <H>2. How We Use Your Information</H>
      <p>We use the information we collect to:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Respond to enquiries and provide customer support</li>
        <li>Operate, maintain and improve our website and the Craftsi platform</li>
        <li>Facilitate connections between customers and service providers</li>
        <li>Send service-related notifications and updates</li>
        <li>Comply with legal obligations</li>
      </ul>
    </div>

    <div className="space-y-4">
      <H>3. Sharing of Information</H>
      <p>
        We do not sell your personal information. We may share information with service
        providers who assist us in operating our platform, with other users as necessary to
        provide marketplace functionality (for example, sharing booking details between a
        customer and a vendor), or where required by law.
      </p>
    </div>

    <div className="space-y-4">
      <H>4. Data Security</H>
      <p>
        We implement reasonable technical and organisational measures to protect your
        information. However, no method of transmission over the internet is completely
        secure, and we cannot guarantee absolute security.
      </p>
    </div>

    <div className="space-y-4">
      <H>5. Data Retention</H>
      <p>
        We retain personal information for as long as necessary to provide our services and
        to comply with legal, accounting or reporting requirements.
      </p>
    </div>

    <div className="space-y-4">
      <H>6. Your Rights</H>
      <p>
        You may request access to, correction of, or deletion of your personal information
        by contacting us. We will respond to requests in accordance with applicable law.
      </p>
    </div>

    <div className="space-y-4">
      <H>7. Third-Party Links</H>
      <p>
        Our website may contain links to third-party websites. We are not responsible for
        the privacy practices of those websites.
      </p>
    </div>

    <div className="space-y-4">
      <H>8. Changes to This Policy</H>
      <p>
        We may update this Privacy Policy from time to time. Updates will be posted on this
        page, and continued use of our services after changes are posted constitutes
        acceptance of the revised policy.
      </p>
    </div>

    <div className="space-y-4">
      <H>9. Contact Us</H>
      <p>
        If you have questions about this Privacy Policy, please contact us through our
        Contact Us page or at: sunkeventerprises@gmail.com.
      </p>
      <p className="text-sm">
        Sunkev Enterprises Private Limited · 19-3-1A/S9, Postal Colony, Korrameenugunta,
        Renigunta Rd, Tirupati – 517501, Andhra Pradesh, India
      </p>
    </div>
  </LegalLayout>
);

export const Terms = () => (
  <LegalLayout eyebrow="Legal" title="Terms & Conditions">
    <p>
      These Terms & Conditions ("Terms") govern your use of the website of Sunkev
      Enterprises Private Limited ("Sunkev Enterprises", "we", "us", "our") and the Craftsi
      platform. By accessing our website or using our services, you agree to these Terms.
    </p>

    <div className="space-y-4">
      <H>1. About Us</H>
      <p>
        Sunkev Enterprises Private Limited is a private limited company registered in India,
        operating in the technology and digital solutions industry. Craftsi, our flagship
        platform, is a local services marketplace connecting customers with businesses and
        service providers.
      </p>
    </div>

    <div className="space-y-4">
      <H>2. Use of Our Services</H>
      <p>You agree to use our website and services only for lawful purposes and in a manner that does not:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Violate any applicable law or regulation</li>
        <li>Infringe the rights of any third party</li>
        <li>Interfere with or disrupt the operation of our platform</li>
        <li>Involve submitting false, misleading or fraudulent information</li>
      </ul>
    </div>

    <div className="space-y-4">
      <H>3. The Craftsi Platform</H>
      <p>
        Craftsi acts as a marketplace that connects customers with independent businesses
        and service providers. Sunkev Enterprises does not itself provide the listed
        services and is not a party to transactions between customers and service providers,
        except as explicitly stated within the platform.
      </p>
    </div>

    <div className="space-y-4">
      <H>4. Intellectual Property</H>
      <p>
        All content, branding, logos and software on this website and the Craftsi platform
        are the property of Sunkev Enterprises Private Limited or its licensors and are
        protected by applicable intellectual property laws. You may not reproduce,
        distribute or create derivative works without our prior written consent.
      </p>
    </div>

    <div className="space-y-4">
      <H>5. Disclaimers</H>
      <p>
        Our website and services are provided on an "as is" and "as available" basis. We
        make no warranties, express or implied, regarding the accuracy, reliability or
        availability of our website or services.
      </p>
    </div>

    <div className="space-y-4">
      <H>6. Limitation of Liability</H>
      <p>
        To the maximum extent permitted by law, Sunkev Enterprises Private Limited shall
        not be liable for any indirect, incidental, special or consequential damages arising
        out of or in connection with the use of our website or services.
      </p>
    </div>

    <div className="space-y-4">
      <H>7. Governing Law</H>
      <p>
        These Terms are governed by and construed in accordance with the laws of India. Any
        disputes arising under these Terms shall be subject to the exclusive jurisdiction of
        the courts in India.
      </p>
    </div>

    <div className="space-y-4">
      <H>8. Changes to These Terms</H>
      <p>
        We may update these Terms from time to time. Updates will be posted on this page,
        and continued use of our website or services after changes are posted constitutes
        acceptance of the revised Terms.
      </p>
    </div>

    <div className="space-y-4">
      <H>9. Contact Us</H>
      <p>
        If you have questions about these Terms, please contact us through our Contact Us
        page or at: sunkeventerprises@gmail.com.
      </p>
      <p className="text-sm">Sunkev Enterprises Private Limited · India</p>
    </div>
  </LegalLayout>
);
